import {
  getAdminPayouts,
  markAdminPayoutPaid,
  payAllAdminPayouts,
  refundAdminPayout,
  getAdminBankingInfo,
  copyAdminBankingInfo,
} from '../../util/api';
import { storableError } from '../../util/errors';

// ================ Action types ================ //

export const FETCH_PAYOUTS_REQUEST = 'app/AdminPayoutsPage/FETCH_PAYOUTS_REQUEST';
export const FETCH_PAYOUTS_SUCCESS = 'app/AdminPayoutsPage/FETCH_PAYOUTS_SUCCESS';
export const FETCH_PAYOUTS_ERROR = 'app/AdminPayoutsPage/FETCH_PAYOUTS_ERROR';

export const MARK_PAID_REQUEST = 'app/AdminPayoutsPage/MARK_PAID_REQUEST';
export const MARK_PAID_SUCCESS = 'app/AdminPayoutsPage/MARK_PAID_SUCCESS';
export const MARK_PAID_ERROR = 'app/AdminPayoutsPage/MARK_PAID_ERROR';

export const PAY_ALL_REQUEST = 'app/AdminPayoutsPage/PAY_ALL_REQUEST';
export const PAY_ALL_SUCCESS = 'app/AdminPayoutsPage/PAY_ALL_SUCCESS';
export const PAY_ALL_ERROR = 'app/AdminPayoutsPage/PAY_ALL_ERROR';
export const CLEAR_PAY_ALL = 'app/AdminPayoutsPage/CLEAR_PAY_ALL';

export const PAY_TESTER_REQUEST = 'app/AdminPayoutsPage/PAY_TESTER_REQUEST';
export const PAY_TESTER_SUCCESS = 'app/AdminPayoutsPage/PAY_TESTER_SUCCESS';
export const PAY_TESTER_ERROR = 'app/AdminPayoutsPage/PAY_TESTER_ERROR';

export const REFUND_REQUEST = 'app/AdminPayoutsPage/REFUND_REQUEST';
export const REFUND_SUCCESS = 'app/AdminPayoutsPage/REFUND_SUCCESS';
export const REFUND_ERROR = 'app/AdminPayoutsPage/REFUND_ERROR';

export const FETCH_BANKING_REQUEST = 'app/AdminPayoutsPage/FETCH_BANKING_REQUEST';
export const FETCH_BANKING_SUCCESS = 'app/AdminPayoutsPage/FETCH_BANKING_SUCCESS';
export const FETCH_BANKING_ERROR = 'app/AdminPayoutsPage/FETCH_BANKING_ERROR';

export const COPY_BANKING_REQUEST = 'app/AdminPayoutsPage/COPY_BANKING_REQUEST';
export const COPY_BANKING_SUCCESS = 'app/AdminPayoutsPage/COPY_BANKING_SUCCESS';
export const COPY_BANKING_ERROR = 'app/AdminPayoutsPage/COPY_BANKING_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchInProgress: false,
  fetchError: null,
  notAuthorized: false,
  rows: [],
  // Per-tester netted balances, every tester's Earnings-page ledger, and the
  // marketplace totals — all computed server-side so the dashboard never does
  // payout math of its own.
  balances: [],
  ledgers: [],
  totals: null,
  markPaidInProgress: [], // transactionIds currently being marked paid
  markPaidError: null,
  payAllInProgress: false,
  payAllError: null,
  lastPayoutRun: null, // receipt from the most recent "Pay all"
  payTesterInProgress: [], // providerIds currently being settled one tester at a time
  payTesterError: null,
  refundInProgress: [], // transactionIds currently being refunded
  refundError: null,
  bankingRows: [],
  bankingLoaded: false, // fetched at least once — the Banking tab loads lazily
  bankingFetchInProgress: false,
  bankingFetchError: null,
  copyBankingInProgress: [], // userIds currently mid-copy
  copyBankingError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_PAYOUTS_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null, notAuthorized: false };
    case FETCH_PAYOUTS_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        notAuthorized: false,
        rows: payload.rows,
        balances: payload.balances,
        ledgers: payload.ledgers,
        totals: payload.totals,
      };
    case FETCH_PAYOUTS_ERROR:
      return {
        ...state,
        fetchInProgress: false,
        notAuthorized: !!payload.notAuthorized,
        fetchError: payload.notAuthorized ? null : payload.error,
        rows: [],
        balances: [],
        ledgers: [],
        totals: null,
      };

    case MARK_PAID_REQUEST:
      return {
        ...state,
        markPaidError: null,
        markPaidInProgress: [...state.markPaidInProgress, payload],
      };
    case MARK_PAID_SUCCESS:
      return {
        ...state,
        markPaidInProgress: state.markPaidInProgress.filter(id => id !== payload.transactionId),
        rows: state.rows.map(row =>
          row.transactionId === payload.transactionId
            ? { ...row, status: 'paid', paidAt: payload.paidAt }
            : row
        ),
      };
    case MARK_PAID_ERROR:
      return {
        ...state,
        markPaidInProgress: state.markPaidInProgress.filter(id => id !== payload.transactionId),
        markPaidError: payload.error,
      };

    case PAY_ALL_REQUEST:
      return { ...state, payAllInProgress: true, payAllError: null, lastPayoutRun: null };
    case PAY_ALL_SUCCESS:
      return { ...state, payAllInProgress: false, lastPayoutRun: payload };
    case PAY_ALL_ERROR:
      return { ...state, payAllInProgress: false, payAllError: payload };
    case CLEAR_PAY_ALL:
      return { ...state, payAllError: null, lastPayoutRun: null };

    case PAY_TESTER_REQUEST:
      return {
        ...state,
        payTesterError: null,
        payTesterInProgress: [...state.payTesterInProgress, payload],
      };
    case PAY_TESTER_SUCCESS:
      return {
        ...state,
        payTesterInProgress: state.payTesterInProgress.filter(id => id !== payload.providerId),
      };
    case PAY_TESTER_ERROR:
      return {
        ...state,
        payTesterInProgress: state.payTesterInProgress.filter(id => id !== payload.providerId),
        payTesterError: payload.error,
      };

    case REFUND_REQUEST:
      return {
        ...state,
        refundError: null,
        refundInProgress: [...state.refundInProgress, payload],
      };
    case REFUND_SUCCESS:
      return {
        ...state,
        refundInProgress: state.refundInProgress.filter(id => id !== payload.transactionId),
        rows: state.rows.map(row =>
          row.transactionId === payload.transactionId
            ? {
                ...row,
                status: payload.status,
                clawbackUsd: payload.clawbackUsd,
                refundedAt: new Date().toISOString(),
              }
            : row
        ),
      };
    case REFUND_ERROR:
      return {
        ...state,
        refundInProgress: state.refundInProgress.filter(id => id !== payload.transactionId),
        refundError: payload.error,
      };

    case FETCH_BANKING_REQUEST:
      return { ...state, bankingFetchInProgress: true, bankingFetchError: null };
    case FETCH_BANKING_SUCCESS:
      return {
        ...state,
        bankingFetchInProgress: false,
        bankingLoaded: true,
        bankingRows: payload.rows,
      };
    case FETCH_BANKING_ERROR:
      return {
        ...state,
        bankingFetchInProgress: false,
        notAuthorized: !!payload.notAuthorized || state.notAuthorized,
        bankingFetchError: payload.notAuthorized ? null : payload.error,
      };

    case COPY_BANKING_REQUEST:
      return {
        ...state,
        copyBankingError: null,
        copyBankingInProgress: [...state.copyBankingInProgress, payload],
      };
    case COPY_BANKING_SUCCESS:
      return {
        ...state,
        copyBankingInProgress: state.copyBankingInProgress.filter(id => id !== payload.userId),
        bankingRows: state.bankingRows.map(row =>
          row.userId === payload.userId
            ? {
                ...row,
                copyCount: payload.copyCount,
                archivedAt: payload.archivedAt,
                bankRoutingNumber: payload.bankRoutingNumber,
                bankAccountNumber: payload.bankAccountNumber,
              }
            : row
        ),
      };
    case COPY_BANKING_ERROR:
      return {
        ...state,
        copyBankingInProgress: state.copyBankingInProgress.filter(id => id !== payload.userId),
        copyBankingError: payload.error,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchPayoutsRequest = () => ({ type: FETCH_PAYOUTS_REQUEST });
const fetchPayoutsSuccess = payload => ({ type: FETCH_PAYOUTS_SUCCESS, payload });
const fetchPayoutsError = payload => ({ type: FETCH_PAYOUTS_ERROR, error: true, payload });

const markPaidRequest = transactionId => ({ type: MARK_PAID_REQUEST, payload: transactionId });
const markPaidSuccess = payload => ({ type: MARK_PAID_SUCCESS, payload });
const markPaidError = payload => ({ type: MARK_PAID_ERROR, error: true, payload });

const payAllRequest = () => ({ type: PAY_ALL_REQUEST });
const payAllSuccess = payload => ({ type: PAY_ALL_SUCCESS, payload });
const payAllErrorAction = payload => ({ type: PAY_ALL_ERROR, error: true, payload });

const payTesterRequest = providerId => ({ type: PAY_TESTER_REQUEST, payload: providerId });
const payTesterSuccess = payload => ({ type: PAY_TESTER_SUCCESS, payload });
const payTesterErrorAction = payload => ({ type: PAY_TESTER_ERROR, error: true, payload });

// Drops the receipt from the last run, so reopening the dialog shows a fresh
// sheet of what's owed now rather than a stale one.
export const clearPayoutRun = () => ({ type: CLEAR_PAY_ALL });

const refundRequest = transactionId => ({ type: REFUND_REQUEST, payload: transactionId });
const refundSuccess = payload => ({ type: REFUND_SUCCESS, payload });
const refundErrorAction = payload => ({ type: REFUND_ERROR, error: true, payload });

const fetchBankingRequest = () => ({ type: FETCH_BANKING_REQUEST });
const fetchBankingSuccess = rows => ({ type: FETCH_BANKING_SUCCESS, payload: { rows } });
const fetchBankingError = payload => ({ type: FETCH_BANKING_ERROR, error: true, payload });

const copyBankingRequest = userId => ({ type: COPY_BANKING_REQUEST, payload: userId });
const copyBankingSuccess = payload => ({ type: COPY_BANKING_SUCCESS, payload });
const copyBankingError = payload => ({ type: COPY_BANKING_ERROR, error: true, payload });

// ================ Thunks ================ //

export const fetchPayouts = () => dispatch => {
  dispatch(fetchPayoutsRequest());
  return getAdminPayouts()
    .then(response => {
      dispatch(
        fetchPayoutsSuccess({
          rows: response.rows || [],
          balances: response.balances || [],
          ledgers: response.ledgers || [],
          totals: response.totals || null,
        })
      );
      return response;
    })
    .catch(e => {
      // 403 → logged in but not on the admin allowlist. Anything else is a real error.
      const notAuthorized = e && (e.status === 403 || e.status === 401);
      dispatch(fetchPayoutsError({ notAuthorized, error: storableError(e) }));
    });
};

export const markPayoutPaid = transactionId => dispatch => {
  dispatch(markPaidRequest(transactionId));
  return markAdminPayoutPaid({ transactionId })
    .then(response => {
      dispatch(markPaidSuccess({ transactionId, paidAt: response.paidAt }));
      // Settling one payout can also recover part of that tester's refund
      // adjustment, so pull fresh balances rather than guessing at them here.
      dispatch(fetchPayouts());
      return response;
    })
    .catch(e => {
      dispatch(markPaidError({ transactionId, error: storableError(e) }));
    });
};

// A settlement can come back 200 with individual testers that didn't get written
// (Sharetribe hiccup mid-run). Those aren't successes, so surface the first one.
const settlementError = settlement => {
  const [failure] = settlement?.failed || [];
  return failure ? storableError(new Error(failure.error || 'Settlement failed')) : null;
};

// One payout run. Pass the transactionIds the run sheet listed so the run settles
// exactly what the admin funded; the server recomputes the amounts either way and
// the receipt it returns is the record of what was actually settled.
export const payAllPayouts = (transactionIds = null) => dispatch => {
  dispatch(payAllRequest());
  return payAllAdminPayouts(transactionIds ? { transactionIds } : {})
    .then(response => {
      dispatch(payAllSuccess(response));
      dispatch(fetchPayouts());
      return response;
    })
    .catch(e => {
      dispatch(payAllErrorAction(storableError(e)));
    });
};

// Settle one tester: the payouts on their card, netted against whatever refund
// adjustment they're carrying. This is the everyday action — one transfer to one
// person — so it stays out of the "Pay everyone" receipt.
export const payTester = (providerId, transactionIds) => dispatch => {
  dispatch(payTesterRequest(providerId));
  return payAllAdminPayouts({ transactionIds })
    .then(response => {
      const error = settlementError(response);
      dispatch(
        error ? payTesterErrorAction({ providerId, error }) : payTesterSuccess({ providerId })
      );
      dispatch(fetchPayouts());
      return response;
    })
    .catch(e => {
      dispatch(payTesterErrorAction({ providerId, error: storableError(e) }));
    });
};

export const refundPayout = (transactionId, reason) => dispatch => {
  dispatch(refundRequest(transactionId));
  return refundAdminPayout({ transactionId, reason })
    .then(response => {
      dispatch(
        refundSuccess({
          transactionId,
          status: response.status,
          clawbackUsd: response.clawbackUsd,
        })
      );
      dispatch(fetchPayouts());
      return response;
    })
    .catch(e => {
      dispatch(refundErrorAction({ transactionId, error: storableError(e) }));
    });
};

export const fetchBankingInfo = () => dispatch => {
  dispatch(fetchBankingRequest());
  return getAdminBankingInfo()
    .then(response => {
      dispatch(fetchBankingSuccess(response.rows || []));
      return response;
    })
    .catch(e => {
      const notAuthorized = e && (e.status === 403 || e.status === 401);
      dispatch(fetchBankingError({ notAuthorized, error: storableError(e) }));
    });
};

// Called each time the admin confirms they've copied a tester's banking info
// into their own records. The server only deletes the raw numbers once this has
// been called REQUIRED_BANKING_COPIES times (see server/api/admin/index.js) — the
// row updates in place either way so the UI can show copy progress.
export const copyBankingInfo = userId => dispatch => {
  dispatch(copyBankingRequest(userId));
  return copyAdminBankingInfo({ userId })
    .then(response => {
      dispatch(
        copyBankingSuccess({
          userId,
          copyCount: response.copyCount,
          archivedAt: response.archivedAt,
          bankRoutingNumber: response.bankRoutingNumber,
          bankAccountNumber: response.bankAccountNumber,
        })
      );
      return response;
    })
    .catch(e => {
      dispatch(copyBankingError({ userId, error: storableError(e) }));
    });
};
